import { Order } from '@/interfaces/order.interface';
import StatusDropdown from '../common/StatusComponent';
import React, { useState } from 'react';
import {
  OrderStatus,
  orderStatusOptions,
  showToastNotification,
} from '@/utility/helper';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import { callApi } from '@/api/callApi';
import Model from '../common/models/Model';
import FormField from '../UI/FormField';
import { RecentOrder } from '@/interfaces/dashboard.interface';
import ConfirmationModel from '../common/models/ConfrimationModel';
import BtnLoader from '../UI/loaders/BtnLoader';
import ErrorMessage from '../common/ErrorMessage';

interface OrderStatusProp {
  order: Order | RecentOrder;
  onSucess: () => void;
}

const OrderStatusUpdate: React.FC<OrderStatusProp> = ({ order, onSucess }) => {
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false);
  const [showUpdateModel, setShowUpdateModel] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [statusToUpdate, setStatusToUpdate] = useState<string | null>(null);

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    const containsNonNumeric = /[a-zA-Z]/.test(val);
    if (val.trim().length >= 5 && containsNonNumeric) {
      setErrorMessage('');
    } else {
      if (val.trim().length < 5) {
        setErrorMessage('Reason must be at least 5 characters long');
      } else if (!containsNonNumeric) {
        setErrorMessage('Reason must contain at least one letter');
      }
    }
    setRejectReason(val);
  };


  const handleOrderStatusUpdate = async (
    id: string,
    newStatus: string,
    rejectReason?: string,
  ) => {
    if (newStatus !== (OrderStatus.CANCELED) && order.paymentMethod.type !== 'COD' && order.paymentStatus === 'UNPAID') {
      showToastNotification('default', 'Payment is pending. Status cannot be updated until payment is received.');
      return;
    }
    if (newStatus === OrderStatus.CANCELED && !rejectReason) {
      setStatusToUpdate(OrderStatus.CANCELED);
      setIsOpen(true);
      return;
    }
    try {
      setLoading(true)
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/update-status`;
      await callApi(PATH, {
        method: 'PATCH',
        data: { id, status: newStatus, rejectReason },
      });

      showToastNotification('success', `Status updated to ${newStatus}`);
      onSucess();
      setRejectReason('');
      setStatusToUpdate(null);
      setLoading(false)
    } catch (error: unknown) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error is', error);
      setLoading(false)
    }
  };

  const filteredOrderStatus =
    order.status === 'PENDING'
      ? [
        {
          value: OrderStatus.ACCEPTED,
          label: 'Accepted',
          textClass: 'text-blue-600',
          bgClass: 'bg-blue-400',
        },
        {
          value: OrderStatus.CANCELED,
          label: 'Canceled',
          textClass: 'text-red-600',
          bgClass: 'bg-red-400',
        },
      ]
      : order.status === OrderStatus.ACCEPTED
        ? [
          {
            value: OrderStatus.OUT_FOR_DELIVERY,
            label: 'Out for Delivery',
            textClass: 'text-yellow-600',
            bgClass: 'bg-yellow-400',
          },
        ]
        : order.status === OrderStatus.FAILED
          ? [
            {
              value: OrderStatus.PENDING,
              label: 'Pending',
              textClass: 'text-yellow-600',
              bgClass: 'bg-yellow-400',
            },
          ]
          : order.status === OrderStatus.CANCELED
            ? []
            : orderStatusOptions.filter(
              (s) =>
                !order.previousStatus.some((prev) => prev.status === s.value),
            );

  const handleSubmitCancellation = () => {
    if (rejectReason.trim().length < 5) {
      showToastNotification(
        'danger',
        'Reason must be at least 5 characters long'
      );
      return;
    }
    if (rejectReason.trim()) {
      handleOrderStatusUpdate(order.id, statusToUpdate!, rejectReason);
    } else {
      showToastNotification(
        'danger',
        'Please provide a valid reason for cancellation',
      );
    }
  };

  return (
    <>
      <StatusDropdown
        value={order.status}
        options={filteredOrderStatus}
        onChange={(newStatus) => { setShowUpdateModel(true), setStatusToUpdate(newStatus) }}
        canEditVal="order"
      />
      <Model
        show={isOpen}
        onClose={() => { setIsOpen(false), setRejectReason('') }}
        title="Write a reason for cancellation"
        height='max-h-88'
      >
        <FormField
          type="textarea"
          rows={6}
          placeholder="Write a valid reason for the cancellation of the order"
          value={rejectReason}
          onChange={(e) => handleReasonChange(e as React.ChangeEvent<HTMLInputElement>)}
        />
        <ErrorMessage message={errorMessage} />
        <div className="mt-5 flex justify-end items-center">
          <button
            type="button"
            disabled={!!errorMessage}
            className="btn-primary"
            onClick={handleSubmitCancellation}
          >
            {loading ? <BtnLoader /> : 'Submit'}
          </button>
        </div>
      </Model>
      {
        showUpdateModel &&
        (
          <>
            <ConfirmationModel
              modelType='update'
              show={showUpdateModel}
              onClose={() => { setShowUpdateModel(false) }}
              onConfirm={() => handleOrderStatusUpdate(order.id, statusToUpdate!)}
              type={`order status to ${statusToUpdate?.toLocaleLowerCase()}`}
              subTitle='You want to update'
            />
          </>
        )
      }
    </>
  );
};

export default OrderStatusUpdate;
